import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  QuestionMark as QuestionIcon,
  Description as LeafIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import setupService, { Setup, SetupNode } from '../services/setup.service';

// Recursive TreeNode component
interface TreeNodeProps {
  node: SetupNode;
  level: number;
  onEdit: (node: SetupNode) => void;
  onDelete: (node: SetupNode) => void;
  onAddChild: (parentNode: SetupNode) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, level, onEdit, onDelete, onAddChild }) => {
  const [expanded, setExpanded] = useState(level < 2);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <>
      <ListItem
        disablePadding
        sx={{ pl: level * 2 }}
        secondaryAction={
          <Box>
            <IconButton size="small" onClick={() => onAddChild(node)} title="Ajouter enfant">
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onEdit(node)} title="Modifier">
              <EditIcon fontSize="small" />
            </IconButton>
            {node.parent !== null && (
              <IconButton size="small" onClick={() => onDelete(node)} title="Supprimer" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      >
        <ListItemButton onClick={() => hasChildren && setExpanded(!expanded)}>
          {hasChildren && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemIcon>
          )}
          {!hasChildren && <ListItemIcon sx={{ minWidth: 32 }} />}
          <ListItemIcon sx={{ minWidth: 32 }}>
            {node.node_type === 'question' ? (
              <QuestionIcon color="warning" />
            ) : (
              <LeafIcon color="success" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={node.text}
            secondary={node.node_type === 'leaf' && node.node_sessions?.length
              ? `${node.node_sessions.length} séance(s) liée(s)`
              : null
            }
            primaryTypographyProps={{
              sx: {
                fontSize: level === 0 ? '1rem' : '0.875rem',
                fontWeight: level === 0 ? 'bold' : 'normal',
              }
            }}
          />
          <Chip
            label={node.node_type === 'question' ? 'Question' : 'Feuille'}
            size="small"
            color={node.node_type === 'question' ? 'warning' : 'success'}
            variant="outlined"
            sx={{ mr: 8 }}
          />
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.children!.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddChild={onAddChild}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Setups: React.FC = () => {
  const [selectedSetup, setSelectedSetup] = useState<Setup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialogs
  const [editNodeDialog, setEditNodeDialog] = useState<{ open: boolean; node: SetupNode | null; isNew: boolean; parentId?: number }>({
    open: false,
    node: null,
    isNew: false,
  });
  const [deleteNodeDialog, setDeleteNodeDialog] = useState<{ open: boolean; node: SetupNode | null }>({
    open: false,
    node: null,
  });

  // Form state
  const [nodeText, setNodeText] = useState('');
  const [nodeType, setNodeType] = useState<'question' | 'leaf'>('question');

  const loadSetupDetails = useCallback(async (setupId: number) => {
    try {
      const setup = await setupService.get(setupId);
      setSelectedSetup(setup);
    } catch (err) {
      console.error('Error loading setup details:', err);
    }
  }, []);

  const loadSetups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Get available (public) setups which includes the default one
      const data = await setupService.getAvailable();
      // Auto-select the default setup
      const defaultSetup = data.find(s => s.name === 'Setup par défaut');
      if (defaultSetup) {
        loadSetupDetails(defaultSetup.id);
      } else if (data.length > 0) {
        loadSetupDetails(data[0].id);
      }
    } catch (err) {
      console.error('Error loading setups:', err);
      setError('Erreur lors du chargement des setups');
    } finally {
      setLoading(false);
    }
  }, [loadSetupDetails]);

  useEffect(() => {
    loadSetups();
  }, [loadSetups]);

  const handleEditNode = (node: SetupNode) => {
    setNodeText(node.text);
    setNodeType(node.node_type);
    setEditNodeDialog({ open: true, node, isNew: false });
  };

  const handleAddChild = (parentNode: SetupNode) => {
    setNodeText('');
    setNodeType('question');
    setEditNodeDialog({ open: true, node: null, isNew: true, parentId: parentNode.id });
  };

  const handleDeleteNode = (node: SetupNode) => {
    setDeleteNodeDialog({ open: true, node });
  };

  const handleSaveNode = async () => {
    if (!selectedSetup || !nodeText.trim()) return;

    try {
      if (editNodeDialog.isNew && editNodeDialog.parentId) {
        // Create new node
        await setupService.createNode(selectedSetup.id, {
          parent: editNodeDialog.parentId,
          node_type: nodeType,
          text: nodeText.trim(),
        });
      } else if (editNodeDialog.node) {
        // Update existing node
        await setupService.updateNode(selectedSetup.id, editNodeDialog.node.id, {
          text: nodeText.trim(),
          node_type: nodeType,
        });
      }
      setEditNodeDialog({ open: false, node: null, isNew: false });
      loadSetupDetails(selectedSetup.id);
    } catch (err) {
      console.error('Error saving node:', err);
      setError('Erreur lors de la sauvegarde');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedSetup || !deleteNodeDialog.node) return;

    try {
      await setupService.deleteNode(selectedSetup.id, deleteNodeDialog.node.id);
      setDeleteNodeDialog({ open: false, node: null });
      loadSetupDetails(selectedSetup.id);
    } catch (err) {
      console.error('Error deleting node:', err);
      setError('Erreur lors de la suppression');
    }
  };

  // Count total nodes
  const countNodes = (nodes: SetupNode[]): number => {
    let count = 0;
    for (const node of nodes) {
      count++;
      if (node.children) {
        count += countNodes(node.children);
      }
    }
    return count;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Gestion des Setups
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Setup selector */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Setup par défaut
        </Typography>
        {selectedSetup ? (
          <Box>
            <Typography variant="body1">
              <strong>{selectedSetup.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {countNodes(selectedSetup.nodes || [])} nœuds •
              Créé par {selectedSetup.created_by_name || 'Système'} •
              {selectedSetup.is_public ? 'Public' : 'Privé'}
            </Typography>
          </Box>
        ) : (
          <Typography color="text.secondary">Aucun setup sélectionné</Typography>
        )}
      </Paper>

      {/* Tree view */}
      {selectedSetup && selectedSetup.nodes && selectedSetup.nodes.length > 0 ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Arbre de décision
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cliquez sur un nœud pour l'éditer. Utilisez les boutons pour ajouter des enfants ou supprimer.
          </Typography>
          <List>
            {selectedSetup.nodes.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                onEdit={handleEditNode}
                onDelete={handleDeleteNode}
                onAddChild={handleAddChild}
              />
            ))}
          </List>
        </Paper>
      ) : selectedSetup ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Ce setup n'a pas encore de nœuds.</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={() => {
              setNodeText('');
              setNodeType('question');
              setEditNodeDialog({ open: true, node: null, isNew: true, parentId: undefined });
            }}
          >
            Créer le nœud racine
          </Button>
        </Paper>
      ) : null}

      {/* Edit/Add Node Dialog */}
      <Dialog open={editNodeDialog.open} onClose={() => setEditNodeDialog({ open: false, node: null, isNew: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editNodeDialog.isNew ? 'Ajouter un nœud' : 'Modifier le nœud'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Type de nœud</Typography>
            <ToggleButtonGroup
              value={nodeType}
              exclusive
              onChange={(_, value) => value && setNodeType(value)}
              fullWidth
              size="small"
            >
              <ToggleButton value="question">
                <QuestionIcon sx={{ mr: 1 }} /> Question
              </ToggleButton>
              <ToggleButton value="leaf">
                <LeafIcon sx={{ mr: 1 }} /> Feuille
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Texte du nœud"
            fullWidth
            multiline
            rows={3}
            value={nodeText}
            onChange={(e) => setNodeText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNodeDialog({ open: false, node: null, isNew: false })}>
            Annuler
          </Button>
          <Button onClick={handleSaveNode} variant="contained" startIcon={<SaveIcon />}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteNodeDialog.open} onClose={() => setDeleteNodeDialog({ open: false, node: null })}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le nœud "{deleteNodeDialog.node?.text}" et tous ses enfants ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteNodeDialog({ open: false, node: null })}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Setups;
